"use client";

import { useEffect, useSyncExternalStore } from "react";

import { LOCAL_STORAGE_KEYS } from "@/shared/config";
import {
  isHeaderLayout,
  type HeaderLayout,
} from "@/shared/types/header-layout";

const ATTR = "data-header-layout";
const EVENT = "header-layout-changed";

// Начальное значение ДОЛЖНО совпадать с getServerSnapshot() ("top"), иначе на
// первом клиентском рендере useSyncExternalStore вернёт значение, отличное от
// SSR-разметки → hydration mismatch (React #418) на страницах с вертикальным
// хедером. Реальный layout из localStorage применяется в HeaderLayoutInit
// (useEffect) уже ПОСЛЕ гидратации. См. KAN-38.
let currentLayout: HeaderLayout = "top";
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((l) => l());
}

function applyHeaderLayout(layout: HeaderLayout) {
  const root = document.documentElement;
  currentLayout = layout;
  if (layout === "top") {
    root.removeAttribute(ATTR);
  } else {
    root.setAttribute(ATTR, layout);
  }
  notify();
}

export function getHeaderLayout(): HeaderLayout {
  return currentLayout;
}

export function setHeaderLayout(layout: HeaderLayout) {
  applyHeaderLayout(layout);
  localStorage.setItem(LOCAL_STORAGE_KEYS.HEADER_LAYOUT, layout);
  window.dispatchEvent(
    new CustomEvent(EVENT, { detail: { layout } }),
  );
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): HeaderLayout {
  return currentLayout;
}

function getServerSnapshot(): HeaderLayout {
  return "top";
}

export function useHeaderLayout(): HeaderLayout {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export function HeaderLayoutInit() {
  useEffect(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEYS.HEADER_LAYOUT);
    const layout = isHeaderLayout(saved) ? saved : "top";
    applyHeaderLayout(layout);

    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ layout: HeaderLayout }>).detail;
      if (detail?.layout && isHeaderLayout(detail.layout)) {
        applyHeaderLayout(detail.layout);
      }
    };
    window.addEventListener(EVENT, handler);
    return () => window.removeEventListener(EVENT, handler);
  }, []);
  return null;
}
